// #region Imports

/* Telestream */
import { MetadataService } from "@user-interface/glim-player";
import {
  ATTACH_VIDEO_ELEMENT,
  MODIFY_AUDIO_SOUND_FIELD,
  SELECT_AUDIO_TRACK,
  SELECT_NEXT_AUDIO_TRACK
} from '@/utils/action-types';
import {
  SET_AUDIO_METER,
  SET_AUDIO_METERS,
  SET_AUDIO_METERS_OFFSET,
  SET_AUDIO_CHANNEL_COUNT,
  SET_PEAK_MODE,
  CLEAR_AUDIO_METERS
} from '@/utils/mutation-types';

// #endregion Imports

// #region Constants

const METER_COUNT = 16;

// #endregion Constants

export const audio = {

  // #region State

  state: {
    meters: Array.from(
      {length: METER_COUNT},
      (element, index) => {
        return {
          index:                     index,
          channel:                   index,
          enabled:                   false,
          rms:                       '',
          rmsPercentage:             100,
          peak:                      '',
          peakPercentage:            100,
          peakInfinite:              -Infinity,
          peakInfinitePercentage:    100,
          peakFourSeconds:           -Infinity,
          peakFourSecondsPercentage: 100,
          peakFourSecondsValues:     []
        }
      }
    ),
    channelCount: 0,
    metersOffset: 0,
    peakMode:     'default'
  },

  // #endregion State

  // #region Mutations

  mutations: {

    [SET_AUDIO_METER](state, meter) {
      /*
       * It would be less code to splice in the updated meter, but that triggers
       * a lot of re-rendering in Vue (because you are modifying the entire array
       * at that point).
       */
      state.meters[meter.index].channel = meter.channel;
      state.meters[meter.index].enabled = meter.enabled;
      state.meters[meter.index].rms = meter.rms;
      state.meters[meter.index].rmsPercentage = meter.rmsPercentage;
      state.meters[meter.index].peak = meter.peak;
      state.meters[meter.index].peakPercentage = meter.peakPercentage;
      state.meters[meter.index].peakInfinite = meter.peakInfinite;
      state.meters[meter.index].peakInfinitePercentage = meter.peakInfinitePercentage;
      state.meters[meter.index].peakFourSeconds = meter.peakFourSeconds;
      state.meters[meter.index].peakFourSecondsPercentage = meter.peakFourSecondsPercentage;
    },

    [SET_AUDIO_METERS](state, meters) {
      meters.forEach((meter) => {
        state.meters[meter.index].channel = meter.channel;
        state.meters[meter.index].enabled = meter.enabled;
        state.meters[meter.index].rms = meter.rms;
        state.meters[meter.index].rmsPercentage = meter.rmsPercentage;
        state.meters[meter.index].peak = meter.peak;
        state.meters[meter.index].peakPercentage = meter.peakPercentage;
        state.meters[meter.index].peakInfinite = meter.peakInfinite;
        state.meters[meter.index].peakInfinitePercentage = meter.peakInfinitePercentage;
        state.meters[meter.index].peakFourSeconds = meter.peakFourSeconds;
        state.meters[meter.index].peakFourSecondsPercentage = meter.peakFourSecondsPercentage;
      });
    },

    [CLEAR_AUDIO_METERS](state) {
      state.meters = Array.from(
        {length: METER_COUNT},
        (element, index) => {
          return {
            index:                     index,
            channel:                   index,
            enabled:                   false,
            rms:                       '',
            rmsPercentage:             100,
            peak:                      '',
            peakPercentage:            100,
            peakInfinite:              -Infinity,
            peakInfinitePercentage:    100,
            peakFourSeconds:           -Infinity,
            peakFourSecondsPercentage: 100,
            peakFourSecondsValues:     []
          }
        }
      )
    },

    [SET_AUDIO_METERS_OFFSET](state, offset) {
      state.metersOffset = offset;
    },

    [SET_AUDIO_CHANNEL_COUNT](state, count) {
      state.channelCount = count;
    },

    [SET_PEAK_MODE](state, peakMode) {
      state.peakMode = peakMode;
    }

  },

  // #endregion Mutations

  // #region Actions

  actions: {

    [ATTACH_VIDEO_ELEMENT](context, element) {
      pollForAudio(context, element);
    },

    [SELECT_AUDIO_TRACK](context, trackIndex) {
      const selectedTrack = context.rootState.videoController.audioTracksList[trackIndex];
      context.rootState.videoController.setCurrentAudioTrack(selectedTrack);
    },

    [SELECT_NEXT_AUDIO_TRACK](context) {
      if (
        (!context.rootState.videoController?.currentAudioTrack) ||
        (context.rootState.videoController?.audioTracksList.length <= 1)
      ) {
        return;
      }

      const currentTrackIndex = context.rootState.videoController.currentAudioTrack.track;
      const nextTrackIndex = (currentTrackIndex + 1) % context.rootState.videoController.audioTracksList.length;
      const nextTrack = context.rootState.videoController.audioTracksList[nextTrackIndex];
      context.rootState.videoController.setCurrentAudioTrack(nextTrack);
    },

    [MODIFY_AUDIO_SOUND_FIELD](context, { soundField, left, right }) {
      context.rootState.videoController.setCurrentSoundfield(soundField, left, right);
    }

  }

  // #endregion Actions

}

function pollForAudio(context, element) {
  updateAudioMeters(context, element);
  requestAnimationFrame(() => pollForAudio(context, element));
}

let meters = Array.from({ length: METER_COUNT }, () => ({
  index:                     0,
  channel:                   0,
  enabled:                   false,
  rms:                       0,
  rmsPercentage:             0,
  peak:                      0,
  peakPercentage:            0,
  peakInfinite:              -Infinity,
  peakInfinitePercentage:    100,
  peakFourSeconds:           -Infinity,
  peakFourSecondsPercentage: 100,
  peakFourSecondsValues:     []
}));

function updateAudioMeters(context, element) {
  const controller = context.rootState.videoController;
  if (!controller || !context.rootState.video.canPlay) {
    clearAudioState(context);
    return;
  }

  // Note: Audio-only content does not have a tracks list, so we pick the first one.
  const currentAudioTrackIndex = controller.currentAudioTrack ? controller.currentAudioTrack.track : 0;
  const currentMetersTrack = getAudioMeterTrack(element, currentAudioTrackIndex);
  if (!currentMetersTrack) {
    clearAudioState(context);
    return;
  }

  let meterCue = MetadataService.searchForCue(currentMetersTrack.cues, element.currentTime);
  if (!meterCue) {
    return;
  }

  const activeChannelCount = meterCue.value.meter.rms.length;
  if (context.state.channelCount !== activeChannelCount) {
    context.commit(SET_AUDIO_CHANNEL_COUNT, activeChannelCount);
  }

  // Make sure the current meter offset is valid:
  let offset = context.state.metersOffset;
  if (offset > activeChannelCount) {
    offset = 0;
    context.commit(SET_AUDIO_METERS_OFFSET, offset);
  }

  for (let channelIndex = offset; channelIndex < (offset + METER_COUNT); channelIndex++) {
    if (channelIndex < activeChannelCount) {
      let rms = meterCue.value.meter.rms[channelIndex];
      let peak = meterCue.value.meter.peak[channelIndex];

      const meter = meters[channelIndex % METER_COUNT];

      meter.index = channelIndex % METER_COUNT;
      meter.channel = channelIndex;
      meter.enabled = true;
      meter.rms = (rms === Number.NEGATIVE_INFINITY) ? '' : rms.toString().slice(0, 5);
      meter.rmsPercentage = Math.min(rms * 100 / -72, 100);
      meter.peak = (peak === Number.NEGATIVE_INFINITY) ? '' : peak.toString().slice(0, 5);
      meter.peakPercentage = Math.round(Math.min(peak * 100 / -72, 100));

      setPeakFourSecondsAndInfinite(context,meter);
    } else {
      const meter = meters[channelIndex % METER_COUNT];
      
      meter.index = channelIndex % METER_COUNT;
      meter.channel = channelIndex;
      meter.enabled = false;
      meter.rms = '';
      meter.rmsPercentage = 100;
      meter.peak = '';
      meter.peakPercentage = 100;
      setPeakFourSecondsAndInfinite(context,meter);
    }

    context.commit(SET_AUDIO_METERS, meters);
  }
}

function setPeakFourSecondsAndInfinite(context, meter) {
  let peak = meter.peak === '' ? -Infinity : meter.peak;
  const now = context.rootState.videoCurrentTime;
  // as this is being fired on each frame we don't want to put elements for the same timestamp to the array
  if (meter.peakFourSecondsValues.length && meter.peakFourSecondsValues[meter.peakFourSecondsValues.length -1].timestamp === now){
    return;
  }

  meter.peakFourSecondsValues.push({
    timestamp:      now,
    peak:           peak,
    peakPercentage: meter.peakPercentage
  });

  //filter out infinity values, no peak then reported
  meter.peakFourSecondsValues = meter.peakFourSecondsValues.filter(
    (entry) => (now - entry.timestamp <= 4) && entry.peak !== -Infinity
  );

  if (!meter.peakFourSecondsValues.length){
    meter.peakFourSecondsPercentage = 100;
    meter.peakFourSeconds = -Infinity;
  }
  else {
    meter.peakFourSecondsPercentage = Math.min(...meter.peakFourSecondsValues.map((entry) => entry.peakPercentage));
    meter.peakFourSeconds = Math.max(...meter.peakFourSecondsValues.map((entry) => entry.peak));
  }

  meter.peakInfinite = Math.max(meter.peakInfinite, peak);
  meter.peakInfinitePercentage = Math.min(meter.peakInfinitePercentage, meter.peakPercentage);
}

function getAudioMeterTrack(element, index) {
  for (let track of element.textTracks) {
    if ((track.kind === 'metadata') && (track.label === `audioMeters-${index}`)) {
      return track;
    }
  }

  return undefined;
}

export function clearAudioState(context) {
  context.commit(CLEAR_AUDIO_METERS);
  context.commit(SET_AUDIO_CHANNEL_COUNT, 0);
  context.commit(SET_AUDIO_METERS_OFFSET, 0);
}

export function clearMeters(){
  meters = Array.from({ length: METER_COUNT }, () => ({
    index:                     0,
    channel:                   0,
    enabled:                   false,
    rms:                       0,
    rmsPercentage:             0,
    peak:                      0,
    peakPercentage:            0,
    peakInfinite:              -Infinity,
    peakInfinitePercentage:    100,
    peakFourSeconds:           -Infinity,
    peakFourSecondsPercentage: 100,
    peakFourSecondsValues:     []
  }));
}
